import { FeatureGate } from '../../components/FeatureGate';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { McpConnectionDto } from '@aiai/shared';
import { listMcpConnections, revokeMcpConnection } from '../../api';
import { qk } from '../../api/queryKeys';
import { errorMessage } from '../../lib/api';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { LoadingState } from '../../components/States';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { SettingsSection } from './common';

/** A one-line value with a copy button (the server URL, the CLI command). */
function CopyLine({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">{label}</p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <code className="flex-1 select-all break-all rounded bg-surface px-2 py-1.5 font-mono text-xs text-ink">
          {value}
        </code>
        <button
          type="button"
          className="btn-secondary btn-sm whitespace-nowrap"
          onClick={() => {
            void navigator.clipboard?.writeText(value);
            setCopied(true);
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

/** How to connect: paste the URL, approve in the browser. No token to copy. */
function ConnectPanel() {
  const url = `${window.location.origin}/api/mcp`;
  return (
    <div className="card space-y-4 p-5">
      <p className="text-xs text-ink-muted">
        Add this server to any MCP client — Claude Code, claude.ai, ChatGPT, Cursor, VS Code. The
        client opens a browser tab where you sign in and approve the connection; there is no
        token to copy. A connection acts as <strong>you</strong>: it sees exactly what you can
        see in each of your workspaces, can file and update issues, and can redeploy prototypes
        you own.
      </p>
      <CopyLine label="Server URL" value={url} />
      <CopyLine label="Claude Code" value={`claude mcp add --transport http aiai ${url}`} />
    </div>
  );
}

function ConnectionsPanel() {
  const queryClient = useQueryClient();
  const [confirmRevoke, setConfirmRevoke] = useState<McpConnectionDto | null>(null);
  const { data: connections, isLoading } = useQuery({
    queryKey: qk.mcpConnections,
    queryFn: listMcpConnections,
  });
  const revoke = useMutation({
    mutationFn: (id: string) => revokeMcpConnection(id),
    onSuccess: () => {
      setConfirmRevoke(null);
      void queryClient.invalidateQueries({ queryKey: qk.mcpConnections });
    },
  });

  return (
    <div className="card p-5">
      {isLoading ? (
        <LoadingState label="Loading connected apps…" />
      ) : connections && connections.length > 0 ? (
        <ul className="divide-y divide-border rounded border border-border">
          {connections.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-ink">{c.clientName}</p>
                <p className="text-xs text-ink-faint">
                  Home workspace {c.workspace.name} · connected{' '}
                  {new Date(c.createdAt).toLocaleDateString()} ·{' '}
                  {c.lastUsedAt ? `last used ${new Date(c.lastUsedAt).toLocaleString()}` : 'not used yet'}
                </p>
              </div>
              <button
                type="button"
                className="btn-danger-ghost whitespace-nowrap"
                onClick={() => setConfirmRevoke(c)}
              >
                Revoke
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-ink-faint">
          No connected apps yet. Add the server URL above to an MCP client to connect one.
        </p>
      )}

      <ConfirmDialog
        open={confirmRevoke !== null}
        title="Revoke connection"
        message={
          confirmRevoke
            ? `Revoke "${confirmRevoke.clientName}"? It stops working immediately and has to be approved again to reconnect.`
            : ''
        }
        confirmLabel="Revoke"
        busy={revoke.isPending}
        error={revoke.isError ? errorMessage(revoke.error, 'Could not revoke the connection') : null}
        onConfirm={() => confirmRevoke && revoke.mutate(confirmRevoke.id)}
        onClose={() => {
          setConfirmRevoke(null);
          revoke.reset();
        }}
      />
    </div>
  );
}

export function McpSettingsSection() {
  return (
    <FeatureGate feature="mcpTokens">
      <McpSettingsPanel />
    </FeatureGate>
  );
}

function McpSettingsPanel() {
  useDocumentTitle('Settings · MCP');

  return (
    <div className="space-y-6">
      <SettingsSection
        title="MCP"
        description="Investigate runs, issues, prototypes and loops from an AI tool through the aiai-loop MCP server. Connections are approved in the browser and can be revoked here at any time."
      >
        <ConnectPanel />
      </SettingsSection>
      <SettingsSection
        title="Connected apps"
        description="Every MCP client you approved. Revoking one cuts it off at once."
      >
        <ConnectionsPanel />
      </SettingsSection>
    </div>
  );
}
